import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    IconButton,
    Typography,
    Box,
    Avatar,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Snackbar,
    Alert
} from '@mui/material';
import {
    Close as CloseIcon,
    AssignmentReturn as DeliveryInIcon,
    Person as PersonIcon
} from '@mui/icons-material';
import { updateData, fetchDeliveryInFromDeliveryOut } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const DeliveryInModal = ({
    open,
    onClose,
    onSave,
    deliveryData = null,
    mode = 'edit'
}) => {
    const { companyId, branchId, token, userId } = useAuth();
    const isViewMode = mode === 'view';

    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showError, setShowError] = useState(false);
    const [formData, setFormData] = useState({
        DeliveryCallId: '',
        CustomerId: '',
        CustomerName: '',
        ContactNumber1: '',
        ContactNumber2: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',
        DeliveryNo: '',
        EstimateNo: '',
        EnquiryNo: '',
        EstimateId: '',
        BillNumber: '',
        BillDate: '',
        EstimateDate: '',
        FinYear: '',
        ProgramDate: '',
        DeliveryDate: '',
        DeliveryReturnDate: '',
        DeliveryOutVehicleNo: '',
        DeliveryInDate: new Date().toISOString().split('T')[0],
        DeliveryInVehicleNo: '',
        DeliveryInVehicleName: '',
        DeliveryOutVehicleName: '',
        EventName: '',
        EventLocation: '',
        EventAddress: '',
        EventDays: '',
        RentDays: '',
        DeliveryOutTime: '',
        DeliveryInTime: new Date().toTimeString().slice(0, 5),
        Remarks: '',
        IsActive: true,
        CreatedBy: '',
        ModifiedBy: '',
        CreatedDate: '',
        ModifiedDate: '',
        items: []
    });

    const [errors, setErrors] = useState({});

    const calculateRentDays = (deliveryDate, deliveryInDate) => {
        if (!deliveryDate || !deliveryInDate) return 0;
        const start = new Date(`${deliveryDate.split('T')[0]}T00:00:00`);
        const end = new Date(`${deliveryInDate.split('T')[0]}T00:00:00`);
        return Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24));
    };

    useEffect(() => {
        if (open && deliveryData) {
            fetchDeliveryInData();
        }
    }, [open, deliveryData]);

    const fetchDeliveryInData = async () => {
        if (!deliveryData || !deliveryData.Id) return;

        setLoading(true);
        try {
            const response = await fetchDeliveryInFromDeliveryOut(companyId, deliveryData.Id, token);
            console.log('DeliveryIn data from API:', response);

            const data = response.response_data || response;
            const delivery = data.DeliveryCall || data;
            const details = data.DeliveryCallDetails || [];
            const deliveryInDate = delivery.DeliveryInCreatedDate || delivery.DeliveryInDate;

            setFormData(prev => ({
                ...prev,
                DeliveryCallId: delivery.Id || deliveryData.Id,
                CustomerId: delivery.CustomerId || '',
                CustomerName: delivery.CustomerName || '',
                ContactNumber1: delivery.ContactNumber1 || '',
                ContactNumber2: delivery.ContactNumber2 || '',
                AddressLine1: delivery.AddressLine1 || '',
                AddressLine2: delivery.AddressLine2 || '',
                Location: delivery.Location || '',
                PinCode: delivery.PinCode || '',
                DeliveryNo: delivery.DeliveryNo || '',
                EstimateNo: delivery.EstimateNo || '',
                EnquiryNo: delivery.EnquiryNo || '',
                EstimateId: delivery.EstimateId || '',
                BillNumber: delivery.BillNumber || '',
                BillDate: delivery.BillDate ? delivery.BillDate.split('T')[0] : '',
                EstimateDate: delivery.EstimateDate ? delivery.EstimateDate.split('T')[0] : '',
                FinYear: delivery.FinYear || '',
                ProgramDate: delivery.ProgramDate ? delivery.ProgramDate.split('T')[0] : '',
                DeliveryDate: delivery.DeliveryDate ? delivery.DeliveryDate.split('T')[0] : '',
                DeliveryReturnDate: delivery.DeliveryReturnDate ? delivery.DeliveryReturnDate.split('T')[0] : '',
                DeliveryInDate: deliveryInDate ? deliveryInDate.split('T')[0] : new Date().toISOString().split('T')[0],
                DeliveryOutVehicleNo: delivery.DeliveryOutVehicleNo || '',
                DeliveryInVehicleNo: delivery.DeliveryInVehicleNo || '',
                DeliveryInVehicleName: delivery.DeliveryInVehicleName || '',
                DeliveryOutVehicleName: delivery.DeliveryOutVehicleName || '',
                EventName: delivery.EventName || '',
                EventLocation: delivery.EventLocation || '',
                EventAddress: delivery.EventAddress || '',
                EventDays: delivery.EventDays || '',
                RentDays: delivery.RentDays !== null && delivery.RentDays !== undefined && delivery.RentDays !== ''
                    ? delivery.RentDays
                    : calculateRentDays(delivery.DeliveryDate, deliveryInDate || delivery.DeliveryReturnDate),
                DeliveryOutTime: delivery.DeliveryOutTime || '',
                DeliveryInTime: delivery.DeliveryInTime || new Date().toTimeString().slice(0, 5),
                Remarks: delivery.Remarks || '',
                IsActive: delivery.IsActive !== undefined ? delivery.IsActive : true,
                CreatedBy: delivery.CreatedBy || '',
                ModifiedBy: delivery.ModifiedBy || '',
                CreatedDate: delivery.CreatedDate || '',
                ModifiedDate: delivery.ModifiedDate || '',
                items: details.map(item => ({
                    Id: item.Id || '',
                    ItemId: item.ItemId || '',
                    ItemName: item.ItemName || '',
                    HSNCode: item.HSNCode || '',
                    Rate: item.Rate || 0,
                    GSTRate: item.GSTRate || 0,
                    DeliveryOutQuantity: item.DeliveryOutQuantity || 0,
                    DeliveryInQuantity: item.DeliveryInQuantity?.toString() || '0',
                    GoodQuantity: item.GoodQuantity?.toString() || '0',
                    DamagedQuantity: item.DamagedQuantity?.toString() || '0',
                    EstimateId: item.EstimateId || '',
                    DeliveryCallId: item.DeliveryCallId || '',
                    IsActive: item.IsActive !== undefined ? item.IsActive : true,
                    CreatedBy: item.CreatedBy || '',
                    ModifiedBy: item.ModifiedBy || '',
                    CreatedDate: item.CreatedDate || '',
                    ModifiedDate: item.ModifiedDate || ''
                }))
            }));
        } catch (error) {
            console.error('Error fetching delivery in data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field, value, itemIndex = null) => {
        if (itemIndex !== null) {
            setFormData(prev => {
                const newItems = [...prev.items];
                newItems[itemIndex] = {
                    ...newItems[itemIndex],
                    [field]: value
                };

                // Auto-calculate: When GoodQuantity is entered, calculate DamagedQuantity as balance
                if (field === 'GoodQuantity') {
                    const good = parseFloat(value) || 0;
                    const outQty = parseFloat(newItems[itemIndex].DeliveryOutQuantity) || 0;

                    // Validation: Good Qty cannot exceed Out Qty
                    if (good > outQty) {
                        setErrorMessage(`Good Quantity (${good}) cannot exceed Delivery Out Quantity (${outQty})`);
                        setShowError(true);
                        newItems[itemIndex].GoodQuantity = '0';
                        newItems[itemIndex].DamagedQuantity = outQty.toString();
                        newItems[itemIndex].DeliveryInQuantity = '0';
                    } else {
                        // Auto-calculate Damaged Qty as balance
                        const damaged = outQty - good;
                        newItems[itemIndex].DamagedQuantity = damaged.toString();
                        // In Qty = Good Qty only
                        newItems[itemIndex].DeliveryInQuantity = good.toString();
                    }
                } else if (field === 'DamagedQuantity') {
                    // If user manually changes Damaged Qty, recalculate Good Qty
                    const damaged = parseFloat(value) || 0;
                    const outQty = parseFloat(newItems[itemIndex].DeliveryOutQuantity) || 0;

                    // Validation: Damaged Qty cannot exceed Out Qty
                    if (damaged > outQty) {
                        setErrorMessage(`Damaged Quantity (${damaged}) cannot exceed Delivery Out Quantity (${outQty})`);
                        setShowError(true);
                        newItems[itemIndex].DamagedQuantity = '0';
                        newItems[itemIndex].GoodQuantity = outQty.toString();
                        newItems[itemIndex].DeliveryInQuantity = outQty.toString();
                    } else {
                        // Auto-calculate Good Qty as balance
                        const good = outQty - damaged;
                        newItems[itemIndex].GoodQuantity = good.toString();
                        // In Qty = Good Qty only
                        newItems[itemIndex].DeliveryInQuantity = good.toString();
                    }
                }

                return {
                    ...prev,
                    items: newItems
                };
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [field]: value,
                ...(field === 'DeliveryInDate' && prev.DeliveryDate
                    ? { RentDays: calculateRentDays(prev.DeliveryDate, value) }
                    : {})
            }));

            if (errors[field]) {
                setErrors(prev => ({
                    ...prev,
                    [field]: ''
                }));
            }
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.DeliveryInDate) newErrors.DeliveryInDate = 'Delivery in date is required';
        // Vehicle number is optional

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setLoading(true);
        try {
            const deliveryCallDetails = formData.items.map(item => ({
                Id: item.Id,
                ItemId: item.ItemId,
                EstimateId: item.EstimateId || null,
                DeliveryCallId: item.DeliveryCallId || formData.DeliveryCallId,
                HSNCode: item.HSNCode || null,
                Rate: parseFloat(item.Rate) || 0,
                GSTRate: parseFloat(item.GSTRate) || 0,
                DeliveryOutQuantity: parseFloat(item.DeliveryOutQuantity) || 0,
                DeliveryInQuantity: parseFloat(item.DeliveryInQuantity) || 0,
                GoodQuantity: parseFloat(item.GoodQuantity) || 0,
                DamagedQuantity: parseFloat(item.DamagedQuantity) || 0,
                IsActive: item.IsActive,
                CreatedBy: item.CreatedBy || parseInt(userId),
                ModifiedBy: parseInt(userId),
                CreatedDate: item.CreatedDate || new Date().toISOString(),
                ModifiedDate: new Date().toISOString(),
                DeliveryInCreatedBy: parseInt(userId),
                DeliveryInModifiedBy: parseInt(userId),
                DeliveryInCreatedDate: formData.DeliveryInDate,
                DeliveryInModifiedDate: formData.DeliveryInDate,
            }));

            const deliveryCall = {
                Id: formData.DeliveryCallId,
                CompanyId: parseInt(companyId),
                BranchId: parseInt(branchId),
                EstimateId: formData.EstimateId || null,
                CustomerId: formData.CustomerId || null,
                CustomerName: formData.CustomerName,
                ContactNumber1: formData.ContactNumber1 || null,
                ContactNumber2: formData.ContactNumber2 || null,
                AddressLine1: formData.AddressLine1 || null,
                AddressLine2: formData.AddressLine2 || null,
                Location: formData.Location || null,
                PinCode: formData.PinCode || null,
                ProgramDate: formData.ProgramDate || null,
                DeliveryDate: formData.DeliveryDate || null,
                DeliveryReturnDate: formData.DeliveryReturnDate || null,
                EventName: formData.EventName || null,
                EventLocation: formData.EventLocation || null,
                EventAddress: formData.EventAddress || null,
                EventDays: parseFloat(formData.EventDays) || 0,
                RentDays: parseFloat(formData.RentDays) || 0,
                DeliveryOutTime: formData.DeliveryOutTime || null,
                DeliveryInTime: formData.DeliveryInTime || null,
                Remarks: formData.Remarks || null,
                DeliveryOutVehicleNo: formData.DeliveryOutVehicleNo || null,
                DeliveryInVehicleNo: formData.DeliveryInVehicleNo || null,
                DeliveryInVehicleName: formData.DeliveryInVehicleName || null,
                DeliveryOutVehicleName: formData.DeliveryOutVehicleName || null,
                IsActive: formData.IsActive,
                CreatedBy: formData.CreatedBy || parseInt(userId),
                ModifiedBy: parseInt(userId),
                CreatedDate: formData.CreatedDate || new Date().toISOString(),
                ModifiedDate: new Date().toISOString(),
                BillDate: formData.BillDate || null,
                EstimateDate: formData.EstimateDate || null,
                BillNumber: formData.BillNumber || 0,
                DeliveryNo: formData.DeliveryNo || null,
                EstimateNo: formData.EstimateNo || null,
                EnquiryNo: formData.EnquiryNo || null,
                FinYear: formData.FinYear || null,
                DeliveryInCreatedBy: parseInt(userId),
                DeliveryInModifiedBy: parseInt(userId),
                DeliveryInCreatedDate: formData.DeliveryInDate,
                DeliveryInModifiedDate: formData.DeliveryInDate,
            };

            const dataToSend = {
                DeliveryCall: deliveryCall,
                DeliveryCallDetails: deliveryCallDetails
            };

            console.log('=== Delivery In Data to Send ===');
            console.log('DeliveryCall:', deliveryCall);
            console.log('DeliveryCallDetails:', deliveryCallDetails);
            console.log('Full payload:', dataToSend);

            const response = await updateData('DeliveryInApi', formData.DeliveryCallId, dataToSend, token);

            console.log('API Response:', response);

            const isSuccess = response && (
                response.responseCode === '1111' ||
                response.responseCode === 1111 ||
                response.success === true
            );

            if (isSuccess) {
                setSuccessMessage('Delivery In saved successfully!');
                setShowSuccess(true);

                setTimeout(() => {
                    onClose();
                    onSave();
                }, 1500);
            }
        } catch (error) {
            console.error('Error saving delivery in:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSuccess = () => {
        setShowSuccess(false);
    };

    const handleCloseError = () => {
        setShowError(false);
    };

    if (!open) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '95vh',
                    width: '90vw',
                    maxWidth: '1200px'
                }
            }}
        >
            <DialogTitle sx={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                p: 1.5
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 28, height: 28 }}>
                            <DeliveryInIcon sx={{ fontSize: 16 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                {isViewMode ? 'View Delivery In' : 'Create Delivery In'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: 'white', p: 0.4 }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 1 }}>
                {loading && <LinearProgress sx={{ mb: 1 }} />}

                <Grid container spacing={1}>
                    {/* Delivery Details Section */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2, mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem', mb: 1.5 }}>
                                Delivery Details
                            </Typography>

                            <Grid container spacing={1}>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Number"
                                        value={formData.DeliveryNo}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Estimate Number"
                                        value={formData.EstimateNo}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Enquiry Number"
                                        value={formData.EnquiryNo}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem', top: '-6px' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Out Date"
                                        type="date"
                                        value={formData.DeliveryDate}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Customer Information */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Avatar sx={{ bgcolor: "#0d766e", mr: 1, width: 20, height: 20 }}>
                                    <PersonIcon sx={{ fontSize: 12 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem' }}>
                                    Customer Information
                                </Typography>
                            </Box>

                            <Grid container spacing={1}>
                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Customer Name"
                                        value={formData.CustomerName}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Contact"
                                        value={formData.ContactNumber1}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Location"
                                        value={formData.Location}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Address"
                                        value={formData.AddressLine1}
                                        InputProps={{ readOnly: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Delivery In Details */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Avatar sx={{ bgcolor: '#10b981', mr: 1, width: 20, height: 20 }}>
                                    <DeliveryInIcon sx={{ fontSize: 12 }} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem' }}>
                                    Delivery In Details
                                </Typography>
                            </Box>

                            <Grid container spacing={1}>
                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Out Date"
                                        type="date"
                                        value={formData.DeliveryDate}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Out Time"
                                        type="time"
                                        value={formData.DeliveryOutTime}
                                        onChange={(e) => handleInputChange('DeliveryOutTime', e.target.value)}
                                        InputProps={{ readOnly: isViewMode }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Out Vehicle Name"
                                        value={formData.DeliveryOutVehicleName}
                                        onChange={(e) => handleInputChange('DeliveryOutVehicleName', e.target.value)}
                                        InputProps={{ readOnly: isViewMode }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Out Vehicle No"
                                        value={formData.DeliveryOutVehicleNo}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Return Date"
                                        type="date"
                                        value={formData.DeliveryReturnDate}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Event Days"
                                        value={formData.EventDays || ''}
                                        InputProps={{ readOnly: true }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Rent Days"
                                        type="number"
                                        value={formData.RentDays ?? ''}
                                        onChange={(e) => handleInputChange('RentDays', e.target.value)}
                                        inputProps={{ min: 0, step: 0.5 }}
                                        InputProps={{ readOnly: isViewMode }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem', bgcolor: '#fef3c7' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem', color: '#f59e0b' }
                                        }}
                                    />
                                </Grid>

                            </Grid>

                            <Grid container spacing={1} sx={{ mt: 0.5 }}>

                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Return Date *"
                                        type="date"
                                        value={formData.DeliveryInDate}
                                        onChange={(e) => handleInputChange('DeliveryInDate', e.target.value)}
                                        error={!!errors.DeliveryInDate}
                                        helperText={errors.DeliveryInDate}
                                        InputProps={{ readOnly: isViewMode }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Return Time"
                                        type="time"
                                        value={formData.DeliveryInTime}
                                        onChange={(e) => handleInputChange('DeliveryInTime', e.target.value)}
                                        InputProps={{ readOnly: isViewMode }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Return Vehicle Name"
                                        value={formData.DeliveryInVehicleName}
                                        onChange={(e) => handleInputChange('DeliveryInVehicleName', e.target.value)}
                                        InputProps={{ readOnly: isViewMode }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={1.5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Delivery Return Vehicle No"
                                        value={formData.DeliveryInVehicleNo}
                                        onChange={(e) => handleInputChange('DeliveryInVehicleNo', e.target.value)}
                                        error={!!errors.DeliveryInVehicleNo}
                                        helperText={errors.DeliveryInVehicleNo}
                                        InputProps={{ readOnly: isViewMode }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Remarks"
                                        value={formData.Remarks}
                                        onChange={(e) => handleInputChange('Remarks', e.target.value)}
                                        InputProps={{ readOnly: isViewMode }}
                                        sx={{
                                            '& .MuiInputBase-root': { height: '28px', fontSize: '0.75rem' },
                                            '& .MuiInputLabel-root': { fontSize: '0.7rem' },
                                            '& .MuiFormHelperText-root': { fontSize: '0.65rem', mt: 0.2 }
                                        }}
                                    />
                                </Grid>

                            </Grid>

                            
                        </Paper>
                    </Grid>

                    {/* Items Table */}
                    <Grid item xs={12}>
                        <Paper elevation={0} sx={{ p: 1, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2d3748', fontSize: '0.75rem', mb: 1.5 }}>
                                Items
                            </Typography>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.4, px: 0.75 }}>Item Name</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.4, px: 0.75, textAlign: 'center' }}>Out Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.4, px: 0.75, textAlign: 'center' }}>Good Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.4, px: 0.75, textAlign: 'center' }}>Damaged Qty</TableCell>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '0.65rem', py: 0.4, px: 0.75, textAlign: 'center' }}>In Qty</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {formData.items.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell sx={{ fontSize: '0.65rem', py: 0.4, px: 0.75 }}>{item.ItemName}</TableCell>
                                                <TableCell sx={{ py: 0.4, px: 0.75, textAlign: 'center' }}>
                                                    <Typography sx={{ fontSize: '0.65rem' }}>{item.DeliveryOutQuantity}</Typography>
                                                </TableCell>
                                                <TableCell sx={{ py: 0.4, px: 0.75, textAlign: 'center' }}>
                                                    <TextField
                                                        type="number"
                                                        value={item.GoodQuantity}
                                                        onChange={(e) => handleInputChange('GoodQuantity', e.target.value, index)}
                                                        size="small"
                                                        disabled={isViewMode}
                                                        sx={{
                                                            width: '70px',
                                                            '& .MuiInputBase-input': { fontSize: '0.65rem', textAlign: 'center', py: 0.4 },
                                                            '& .MuiInputBase-root': { height: '24px' }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 0.4, px: 0.75, textAlign: 'center' }}>
                                                    <TextField
                                                        type="number"
                                                        value={item.DamagedQuantity}
                                                        onChange={(e) => handleInputChange('DamagedQuantity', e.target.value, index)}
                                                        size="small"
                                                        disabled={isViewMode}
                                                        sx={{
                                                            width: '70px',
                                                            '& .MuiInputBase-input': { fontSize: '0.65rem', textAlign: 'center', py: 0.4 },
                                                            '& .MuiInputBase-root': { height: '24px' }
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ py: 0.4, px: 0.75, textAlign: 'center' }}>
                                                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 600 }}>{item.DeliveryInQuantity}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 1, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    disabled={loading}
                    sx={{
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        py: 0.4,
                        px: 1.5
                    }}
                >
                    {isViewMode ? 'Close' : 'Cancel'}
                </Button>
                {!isViewMode && (
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading}
                        sx={{
                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            py: 0.4,
                            px: 1.5
                        }}
                    >
                        {loading ? 'Saving...' : 'Save'}
                    </Button>
                )}
            </DialogActions>

            {/* Success Message Snackbar */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={1500}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSuccess}
                    severity="success"
                    sx={{
                        width: '100%',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>

            {/* Error Message Snackbar */}
            <Snackbar
                open={showError}
                autoHideDuration={3000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseError}
                    severity="error"
                    sx={{
                        width: '100%',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default DeliveryInModal;
